// The engineering ticket flow (26 October 2026, docs/designs/engineering-tickets-
// and-product-planning.md). Every engineering ticket carries a mode (who drives
// it), a holder (who must act next) and a flag (a person is needed, with the
// origin that raised it). The server stores them and writes them only through
// the flow actions; the screens read them, never rebuild them. The closed lists
// are served at GET /tickets/flow-vocabulary. Staff only: a library never sees
// an engineering ticket at all.

import { agentStatusWords } from './ticketVocabulary.js';
import { formatDate } from './dates.js';

export type TicketDriveMode = 'agent' | 'engineer';
export type TicketHolder = 'agent' | 'product' | 'engineer' | 'reporter';

/** The flow facts on a ticket, as the server sends them. */
export interface TicketFlowFacts {
	drive_mode?: TicketDriveMode | '';
	holder_kind?: TicketHolder | '';
	holder_label?: string;
	holder_since?: string | null;
	needs_attention?: boolean;
	flag_origin?: string;
	flag_reason?: string;
	/** What the flag asks of product: approval (Approve work) or engineering (Assign engineer). */
	flag_need?: '' | 'approval' | 'engineering';
	flag_raised_at?: string | null;
	release_requested_at?: string | null;
	released_at?: string | null;
	agent_last_activity_at?: string | null;
	quiet?: boolean;
}

/** GET /tickets/flow-vocabulary. */
export interface TicketFlowVocabulary {
	modes: string[];
	holders: string[];
	flag_origins: string[];
	flag_needs?: string[];
	stages: string[];
	events: string[];
	verdicts: string[];
	agent_mode: 'on' | 'off';
}

/** The agent log's summary of a ticket with entries (who holds it is the ticket's own holder_kind). */
export interface TicketAgentState {
	agent_slug: string;
	agent_label: string;
	stage: string;
	event: string;
	verdict: string;
	since: string;
	entries: number;
	summary: string;
}

export interface TicketAgentLogEntry {
	id: number;
	ticket_id: number;
	agent_slug: string;
	agent_label: string;
	stage: string;
	event: string;
	verdict: string;
	body: string;
	link_url: string;
	link_label: string;
	/** Set on a hand-over (event handed_over) only: who the ticket was passed to. */
	holder_kind?: '' | TicketHolder;
	holder_slug?: string;
	holder_label?: string;
	/** The origin a flag_raised or flag_cleared entry is about. */
	flag_origin?: string;
	created_by: number | null;
	created_by_name: string;
	created_at: string;
}

/** A free-form agent log entry. Hand-overs and the other flow events go through the flow actions. */
export interface TicketAgentLogInput {
	agent_slug?: string;
	agent_label?: string;
	stage: string;
	event: string;
	verdict?: string;
	body?: string;
	link_url?: string;
	link_label?: string;
}

/** Who a hand-over entry passed the ticket to, in words. */
export function holderWords(e: Pick<TicketAgentLogEntry, 'holder_kind' | 'holder_label'>): string {
	if (e.holder_kind === 'engineer') return 'an engineer';
	if (e.holder_kind === 'reporter') return 'the reporter';
	if (e.holder_kind === 'product') return 'product';
	return e.holder_label ? `the ${e.holder_label.charAt(0).toLowerCase()}${e.holder_label.slice(1)}` : 'an agent';
}

/** The one chip on a list row or board card for an agent-driven ticket: who holds it. '' for an engineer's own. */
export function ticketHolderWords(t: TicketFlowFacts | null | undefined): string {
	if (!t || t.drive_mode !== 'agent') return '';
	switch (t.holder_kind) {
		case 'reporter': return 'With the reporter';
		case 'product': return 'Waiting on product';
		case 'engineer': return 'With an engineer';
		default: return 'With an agent';
	}
}

const ORIGIN_WORDS: Record<string, string> = {
	review: 'ticket review', code_review: 'code review', test: 'testing', release: 'release', reporter: 'the reporter', imported: 'Jira',
};

/** A flag's origin in words: "code review", "the reporter". */
export function flagOriginWords(origin: string | undefined): string {
	return ORIGIN_WORDS[origin ?? ''] ?? (origin || '');
}

/** "Last agent activity 2d ago", from the later of when the agent got it and its last entry. */
export function lastActivityWords(t: TicketFlowFacts): string {
	const times = [t.holder_since, t.agent_last_activity_at].filter((x): x is string => !!x).sort();
	const last = times[times.length - 1];
	return last ? `Last agent activity ${formatDate(last, 'relative')}` : '';
}

// The support side's chip for an engineering ticket an agent is driving: the
// requester's words for where their change is, not engineering's step names.
// The words sit on the one table (utils/ticketVocabulary.ts, `agentWords`).
export function agentLinkWords(status: string): string {
	return agentStatusWords(status);
}

// Words for a stage and an event on the log, and a tone for a verdict chip.
const STAGE_WORDS: Record<string, string> = {
	review: 'Review', build: 'Build', code_review: 'Review layers', test: 'Testing', release: 'Release', engineer: 'Engineer',
};
const EVENT_WORDS: Record<string, string> = {
	started: 'started', progress: 'in progress', finished: 'finished', flagged: 'flagged', handed_back: 'handed back', released: 'released', handed_over: 'handed over',
	mode_changed: 'mode changed', flag_raised: 'flagged for product', flag_cleared: 'flag answered', assigned_engineer: 'assigned to an engineer', release_requested: 'release requested',
};
const VERDICT_WORDS: Record<string, string> = {
	agent_can_build: 'Agent can build', needs_engineer: 'Needs an engineer', needs_author: 'Needs the reporter',
	passed: 'Passed', failed: 'Failed', ready_for_release: 'Ready for release', released: 'Released',
};

export function agentStageWords(stage: string): string { return STAGE_WORDS[stage] ?? stage; }
export function agentEventWords(event: string): string { return EVENT_WORDS[event] ?? event; }
export function agentVerdictWords(verdict: string): string { return VERDICT_WORDS[verdict] ?? verdict; }
export function agentVerdictTone(verdict: string): 'green' | 'blue' | 'amber' | 'red' | 'grey' {
	switch (verdict) {
		case 'agent_can_build':
		case 'passed':
		case 'released': return 'green';
		case 'ready_for_release': return 'blue';
		case 'needs_engineer':
		case 'failed': return 'red';
		case 'needs_author': return 'amber';
		default: return 'grey';
	}
}

// ---- Who holds the ticket: the list's filter ---------------------------------

/** The "Who holds it" filter's choices, in order; the value is what `holder=` sends. */
export const HOLDER_FILTER_OPTIONS: ReadonlyArray<{ value: TicketHolder; label: string }> = [
	{ value: 'agent', label: 'An agent' },
	{ value: 'reporter', label: 'The reporter' },
	{ value: 'product', label: 'Product' },
	{ value: 'engineer', label: 'An engineer' },
];

/** The words a board's filter line uses for a `holder=` list ('' for none or an unknown value). */
export function holderFilterWords(holder: string): string {
	const names = holder.split(',').map((v) => HOLDER_FILTER_OPTIONS.find((o) => o.value === v.trim())?.label).filter((x): x is string => !!x);
	return names.length ? `held by ${names.map((n) => n.toLowerCase()).join(' or ')}` : '';
}

/** Keeps only the values `holder=` accepts, in the order given, without repeats. */
export function cleanHolderParam(raw: string | null | undefined): string {
	const ok = new Set<string>(HOLDER_FILTER_OPTIONS.map((o) => o.value));
	const out: string[] = [];
	for (const v of (raw ?? '').split(',')) { const t = v.trim(); if (ok.has(t) && !out.includes(t)) out.push(t); }
	return out.join(',');
}

// ---- The vocabulary, loaded once a session ------------------------------------

let vocab: Promise<TicketFlowVocabulary> | null = null;

/** GET /tickets/flow-vocabulary, once a session (a failure is not cached). */
export function loadTicketFlowVocabulary(load: () => Promise<TicketFlowVocabulary>): Promise<TicketFlowVocabulary> {
	if (!vocab) {
		// A loader that throws instead of rejecting (a partial mock, say) reads as a failure too.
		vocab = Promise.resolve().then(load).catch((err) => { vocab = null; throw err; });
	}
	return vocab;
}

/** For tests: forget the loaded vocabulary. */
export function resetTicketFlowVocabularyForTest(): void { vocab = null; }
